/**
 * SpeedGauge.tsx
 *
 * Semicircular SVG arc-needle gauge for the Speed Test result. A tick-marked
 * arc track, a value arc colored by speed band (bad→good), and a needle drawn
 * to a computed tip on the arc (no CSS transform — reliable across browsers).
 * The numeric readout + unit + label render as HTML *below* the arc so nothing
 * overlaps the needle hub.
 *
 * Chrome (track / ticks / needle) uses theme CSS variables; the speed-band ramp
 * uses explicit data-viz colors, mirroring the existing SPEED_COLOR_RANGES.
 */

import React from 'react';

export interface SpeedGaugeColorStop {
  /** Upper bound (exclusive) of this band, in the gauge's unit. */
  threshold: number;
  color: string;
}

export interface SpeedGaugeProps {
  value: number;
  max: number;
  label: string;
  unit?: string;
  /** Low→high speed bands (bad→good). Last stop should use threshold: Infinity. */
  colorRanges?: SpeedGaugeColorStop[];
  /** Max rendered width in px. */
  size?: number;
  /** Entrance-animation stagger (ms). */
  animationDelayMs?: number;
}

const DEFAULT_RANGES: SpeedGaugeColorStop[] = [
  { threshold: 25, color: '#ef4444' }, // red — slow
  { threshold: 60, color: '#f59e0b' }, // amber — moderate
  { threshold: Infinity, color: '#10b981' }, // green — fast
];

// SVG geometry. Pivot (needle base + hub) at the bottom-center of the arc.
const CX = 100;
const CY = 100;
const R = 84;
const SW = 14;

/** Point on a circle around the pivot. 0°=right, 90°=up, 180°=left (y is down in SVG). */
function polar(angleDeg: number, radius = R) {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: CX + radius * Math.cos(rad), y: CY - radius * Math.sin(rad) };
}

/**
 * Arc over the top semicircle for fraction range [f0,f1] (0=left … 1=right).
 * Every sub-arc here is ≤180°, so the large-arc-flag is always 0; sweep=1
 * traces left→right over the top in SVG's y-down space.
 */
function arc(f0: number, f1: number): string {
  const p0 = polar(180 - 180 * f0);
  const p1 = polar(180 - 180 * f1);
  return `M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${R} ${R} 0 0 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
}

function bandColor(value: number, ranges: SpeedGaugeColorStop[]): string {
  for (const r of ranges) if (value < r.threshold) return r.color;
  return ranges[ranges.length - 1].color;
}

export const SpeedGauge: React.FC<SpeedGaugeProps> = ({
  value,
  max,
  label,
  unit = 'Mbps',
  colorRanges = DEFAULT_RANGES,
  size = 200,
  animationDelayMs = 0,
}) => {
  const v = Number.isFinite(value) ? value : 0;
  const frac = max > 0 ? Math.max(0, Math.min(1, v / max)) : 0;
  const color = bandColor(v, colorRanges);
  const tip = polar(180 - 180 * frac, R); // needle tip rides the arc centerline
  const ticks = Array.from({ length: 9 }, (_, i) => i / 8);

  return (
    <div
      className="flex flex-col items-center animate-in fade-in slide-in-from-bottom-2 duration-500"
      style={{ animationDelay: `${animationDelayMs}ms`, animationFillMode: 'backwards' }}
    >
      <svg
        viewBox="0 0 200 112"
        width="100%"
        style={{ maxWidth: size }}
        role="img"
        aria-label={`${label}: ${v} ${unit.trim()}`}
      >
        {/* Track. NB: theme CSS vars are raw HSL channels (e.g. "0 0% 45.1%"),
            so they MUST be wrapped in hsl() — a bare var() is an invalid color
            and an invalid stroke renders as `none` (nothing draws). */}
        <path
          d={arc(0, 1)}
          fill="none"
          stroke="hsl(var(--muted-foreground) / 0.25)"
          strokeWidth={SW}
          strokeLinecap="round"
        />
        {/* Value arc */}
        {frac > 0.001 && (
          <path d={arc(0, frac)} fill="none" stroke={color} strokeWidth={SW} strokeLinecap="round" />
        )}
        {/* Tick marks just outside the track */}
        {ticks.map((tf, i) => {
          const a = 180 - 180 * tf;
          const p1 = polar(a, R + SW / 2 + 2);
          const p2 = polar(a, R + SW / 2 + 6);
          return (
            <line
              key={i}
              x1={p1.x}
              y1={p1.y}
              x2={p2.x}
              y2={p2.y}
              stroke="hsl(var(--muted-foreground) / 0.45)"
              strokeWidth={1.5}
            />
          );
        })}
        {/* Needle (drawn to a computed tip) + hub */}
        <line x1={CX} y1={CY} x2={tip.x} y2={tip.y} stroke="hsl(var(--foreground))" strokeWidth={3} strokeLinecap="round" />
        <circle cx={CX} cy={CY} r={6} fill="hsl(var(--foreground))" />
      </svg>
      <div className="-mt-1 flex flex-col items-center">
        <div className="text-3xl font-bold tabular-nums leading-none text-foreground">
          {v.toFixed(v >= 100 ? 0 : 1)}
        </div>
        <div className="mt-0.5 text-[11px] text-muted-foreground">{unit.trim()}</div>
        <div className="mt-1 text-sm font-medium text-foreground">{label}</div>
      </div>
    </div>
  );
};
